import { ItemType } from '@mimir/common/dto/itemTypes';
import { SortOptions, SortOrder, type SortParameters } from '@mimir/common/dto/searchModels';
import type { SearchResultHit, ClientItem } from '@mimir/common/dto/searchResultHit';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { MEDIA_DURATION_SORT_PROPERTY_ID, SYSTEM_FIELD_ID_QUERYPARAM, TECHNICAL_FIELD_ID_QUERYPARAM } from '../search/interfaces';
import { getTechnicalMetadataValue, ComputedTechnicalField, TechnicalAudioAndVideoDataField } from '@mimir/common/util/technicalMetadata';
import { getIndexedMediaDurationMsOrNull } from '@mimir/common/util/item';
import { safeArrayAccess } from '../util/safeArrayAccess';

/**
 * Determines the optimal position for injecting a new item into search results
 * while maintaining the current sorting order.
 *
 * @param newItem - The item to be injected into the search results
 * @param sortParamsList - Current sort parameters (only compact view supports multiple sort)
 * @param currentItems - Search result items
 * @param isAllItemsLoaded - Whether all search result items have been loaded
 * @param isCompactView - Whether the view is in compact mode
 * @param sortFieldsInfo - Mapping of fieldIDs to their respective IDs
 * @returns Index where the new item should be injected.
 * Returns `null` when the item would belong on a page that hasn't been loaded yet or the item is already in the list.
 */
export function getInjectionIndex(
  newItem: ClientItem,
  sortParamsList: SortParameters[],
  currentItems: SearchResultHit[],
  isAllItemsLoaded: boolean,
  isCompactView: boolean,
  sortFieldsInfo: Record<string, { id: string }>,
): number | null {
  if (currentItems.length === 0) return 0;
  const singleSortParam = sortParamsList[0] as SortParameters | undefined;

  // Check if the new item is already in the current items
  if (currentItems.findIndex(item => item.id === newItem.id) !== -1) return null;

  if (singleSortParam?.property === SortOptions.RELEVANCE) {
    // Special handling for RELEVANCE sort:
    // Relevance sort is done by OpenSearch and cannot be replicated client-side
    // We only inject new items when all search results are loaded
    // New items are always placed at the end of the list irrespective of sort order
    return isAllItemsLoaded ? currentItems.length : null;
  }


  let firstNonFolderIndex = 0;
  for (let i = 0; i < currentItems.length; i++) {
    if (safeArrayAccess(currentItems, i).itemType !== ItemType.FOLDER) {
      firstNonFolderIndex = i;
      break;
    }
  }

  let low = firstNonFolderIndex;
  let high = currentItems.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    const currentItem = safeArrayAccess(currentItems, mid);

    const comparison = isCompactView
      ? compareItemsForCompactView(newItem, currentItem, sortParamsList, sortFieldsInfo)
      : compareItems(newItem, currentItem, singleSortParam);
    if (comparison < 0) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  const insertIndex = low;
  // If the new item index is the last index and not all items are loaded then we should not inject
  // the new item as it would belong to a different position if all items are loaded.
  if (isAllItemsLoaded || insertIndex < currentItems.length) {
    return insertIndex;
  }

  return null;
}

function compareItemsForCompactView(
  item1: ClientItem | SearchResultHit,
  item2: ClientItem | SearchResultHit,
  sortParamsList: SortParameters[],
  sortFieldsInfo: Record<string, { id: string }>,
): number {
  if (item1.itemType === ItemType.FOLDER && item2.itemType !== ItemType.FOLDER) {
    return -1;
  }
  if (item1.itemType !== ItemType.FOLDER && item2.itemType === ItemType.FOLDER) {
    return 1;
  }

  const primarySort = safeArrayAccess(sortParamsList, 0);
  const isPrimaryAscending = primarySort.order === SortOrder.ASCENDING;
  const primarySortVal1 = getSortKeyValueFromItem(item1, primarySort, sortFieldsInfo);
  const primarySortVal2 = getSortKeyValueFromItem(item2, primarySort, sortFieldsInfo);

  if (primarySortVal1 !== primarySortVal2) {
    // Null values should always appear at the end, regardless of sort direction
    if (primarySortVal1 === null) return 1;
    if (primarySortVal2 === null) return -1;

    const result = primarySortVal1 < primarySortVal2 ? -1 : 1;
    return isPrimaryAscending ? result : -result;
  }

  if (sortParamsList.length > 1) {
    const secondarySort = safeArrayAccess(sortParamsList, 1);
    const isSecondaryAscending = secondarySort.order === SortOrder.ASCENDING;
    const secondarySortVal1 = getSortKeyValueFromItem(item1, secondarySort, sortFieldsInfo);
    const secondarySortVal2 = getSortKeyValueFromItem(item2, secondarySort, sortFieldsInfo);

    if (secondarySortVal1 === null && secondarySortVal2 === null) return 0;
    if (secondarySortVal1 === null) return 1;  // Always put nulls at the end
    if (secondarySortVal2 === null) return -1; // Always put nulls at the end

    const result = secondarySortVal1 < secondarySortVal2 ? -1 : 1;
    return isSecondaryAscending ? result : -result;
  }

  return 0;
}

function compareItems(item1: ClientItem | SearchResultHit, item2: ClientItem | SearchResultHit, sortParams: SortParameters | undefined): number {
  if (!sortParams || !isSortOptionString(sortParams.property)) return 0;
  const { property, order } = sortParams;

  const value1 = getStandardPropertyValue(item1, property);
  const value2 = getStandardPropertyValue(item2, property);

  if (value1 === null && value2 === null) return 0;
  if (value1 === null) return order === SortOrder.ASCENDING ? 1 : -1;
  if (value2 === null) return order === SortOrder.ASCENDING ? -1 : 1;

  if (value1 === value2) return 0;
  const result = value1 < value2 ? -1 : 1;
  return order === SortOrder.ASCENDING ? result : -result;
}

function getStandardPropertyValue(item: ClientItem | SearchResultHit, property: SortOptions): string | number | null {
  if (!property) return null;
  switch (property) {
    case SortOptions.TITLE: {
      const title = (item.metadata?.formData?.[SystemDefaultField.Title] as string) || item.name || '';
      return title.toLowerCase();
    }

    case SortOptions.ITEM_CREATED_DATE: {
      const createdOn = item.metadata?.formData?.[SystemDefaultField.CreatedOn] as string ??
                      item.metadata?.formData?.[SystemDefaultField.MediaCreatedOn] as string;
      return createdOn ? new Date(createdOn).getTime() : null;
    }

    case SortOptions.MEDIA_CREATED_DATE: {
      const mediaCreatedOn = item.metadata?.formData?.[SystemDefaultField.MediaCreatedOn] as string;
      return mediaCreatedOn ? new Date(mediaCreatedOn).getTime() : null;
    }

    case SortOptions.MODIFIED_ON:
      const modifiedOn = item.modifiedOn ?? item.metadata?.formData?.[SystemDefaultField.CreatedOn] as string;
      return modifiedOn ? new Date(modifiedOn).getTime() : null;

    case SortOptions.MEDIA_SIZE:
      return typeof item.mediaSize === 'number' ? item.mediaSize : null;

    case SortOptions.MEDIA_DURATION:
      return getIndexedMediaDurationMsOrNull(item);

    case SortOptions.PLANNED_INGEST_TIME:
      const startTime = item.scheduledRecording?.startTimeMs;
      return startTime ? new Date(startTime).getTime() : null;

    case SortOptions.RELEVANCE:
    case SortOptions.PINNED:
    case SortOptions.ITEM_DELETION_DATE:
      return null;

    default:
      console.warn(`Unhandled status: property ${property}`);
      // Handle new SortOptions properties here.
      const _exhaustiveCheck: never = property;
      void _exhaustiveCheck;
      throw new Error(`Unhandled status: property ${property}`);
  }
}

function getSortKeyValueFromItem(item: ClientItem | SearchResultHit, sortOption: SortParameters, sortFieldsInfo: Record<string, { id: string }>): string | number | null {
  const { property, fieldId, mdfSource } = sortOption;


  // Handle standard sort options first
  if (isSortOptionString(property)) {
    return getStandardPropertyValue(item, property);
  }

  if (!mdfSource || !fieldId) return null;

  let value: any = undefined;

  // Check system fields
  if (mdfSource === SYSTEM_FIELD_ID_QUERYPARAM) {
    if (fieldId === MEDIA_DURATION_SORT_PROPERTY_ID) {
      return getIndexedMediaDurationMsOrNull(item);
    }
    if (fieldId in item) {
      value = item[fieldId as keyof typeof item];
    }
  } else if (mdfSource === TECHNICAL_FIELD_ID_QUERYPARAM) {
    if (fieldId === TechnicalAudioAndVideoDataField.Duration) {
      return getIndexedMediaDurationMsOrNull(item);
    }
    // Some technical metadata fields may have different fieldId in different item types
    // e.g. 'width', 'height' for images and videos.
    // We need to check if the fieldId is a computed field or a regular field and get the value accordingly.
    const computedField = Object.values(ComputedTechnicalField).find(i => i === fieldId);

    if (computedField) {
      value = getTechnicalMetadataValue(item.technicalMetadata, computedField);
    } else if (item.technicalMetadata?.formData) {
      value = item.technicalMetadata.formData[fieldId];
    }
  }
  const id = sortFieldsInfo[fieldId]?.id;
  if (id && item.metadata?.formData && id in item.metadata.formData) {
    value = item.metadata.formData[id];
  }

  return normalizeValue(value);
}

function normalizeValue(value: any): string | number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' && (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.exec(value))) {
    return new Date(value).getTime();
  }

  if (typeof value === 'string') {
    return value.toLowerCase();
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value;
}

function isSortOptionString(s: string | undefined): s is SortOptions {
  if (!s) return false;
  return Object.values(SortOptions).includes(s as SortOptions);
}
